import React, { useEffect, useState } from 'react';
import { TileCard, EmpButton as Button } from '@empowernow/ui';
import { useQuery } from '@tanstack/react-query';
import { message, Space } from 'antd';
import { api } from '../core/services/api';
import { useBatchAuthorization } from '../core/auth/useBatchAuthorization';
import { PreviewGrid } from './PreviewGrid';
import { FormModal } from './FormModal';
import { AdvancedSearchBar } from './AdvancedSearchBar';
import { GridViewSwitcher } from './GridViewSwitcher';
import { useGridView } from '../core/hooks/useGridView';
import { FormModalSkeleton } from './FormModalSkeleton';
import { useForm } from '../core/hooks/useForm';
import { FormDefinition } from './types';
import { executeGraphWorkflow, resumeGraphWorkflow } from '../core/services/workflowExecution';

type GridDef = {
  id: string;
  name?: string;
  gridConfig?: {
    dataSource?: {
      system?: string;
      objectType?: string;
      action?: string;
      params?: any;
      searchParams?: Record<string, string>;
      isServerSidePagination?: boolean;
    };
    columns?: Array<{
      key: string;
      title: string;
      type?: string;
    }>;
  };
  actions?: any[];
};

type PageDef = {
  id?: string;
  title?: string;
  description?: string;
  grids?: GridDef[];
  actions?: any[];
};

type ActionConfig = {
  id: string;
  label?: string;
  workflowName?: string;
  formId?: string;
  contextual?: boolean;
  acceptsArrayOfItems?: boolean;
  parameterMapping?: Record<string, string>;
};



type Props = {
  page: PageDef;
  hideExitButton?: boolean;
  autoLoadData?: boolean;
  onExit?: () => void;
};

export function PreviewMode({ page, hideExitButton = true, autoLoadData = false, onExit }: Props) {
  const [selectedGrid, setSelectedGrid] = useState(page.grids?.[0]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<Record<string, string>>({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [dataFetchEnabled, setDataFetchEnabled] = useState(autoLoadData);
  
  // Workflow and form modal state
  const [formId, setFormId] = useState<string>("");
  const [workflowName, setWorkflowName] = useState<string>("");
  const [activeRowsData, setActiveRowsData] = useState<Record<string, any>[] | null>(null);
  const [prePopulatedData, setPrePopulatedData] = useState<Record<string, string>>({});
  const [parameterMapping, setParameterMapping] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string>("");
  
  const { form, isLoading: formLoading } = useForm(formId);
  
  const [activeForm, setActiveForm] = useState<{
    form: FormDefinition;
    currentTaskId: string | null;
    workflowId?: string | null;
    workflowName?: string;
  } | null>(null);
  
  // Grid view state
  const { view: gridView, setView: setGridView } = useGridView();

  // Debug logging
  React.useEffect(() => {
    console.log('PreviewMode - page data:', page);
    console.log('PreviewMode - page actions:', page?.actions);
    console.log('PreviewMode - grids:', page?.grids);
    console.log('PreviewMode - selected grid:', selectedGrid);
    console.log('PreviewMode - selected grid actions:', selectedGrid?.actions);
  }, [page, selectedGrid]);

  const grids: GridDef[] = page?.grids ?? [];
  const actions = page?.actions ?? [];

  // Build batch requests for grids and actions as widgets
  const pageId: string | undefined = (page as any)?.id ? String((page as any).id) : undefined;
  const evals = React.useMemo(() => {
    const reqs: any[] = [];
    if (pageId) {
      for (const g of grids) {
        const widgetId = `grid:${g.id || 'idx'}`;
        reqs.push({
          key: widgetId,
          resource: { type: 'page.widget', id: `${pageId}:${widgetId}`, properties: { widgetType: 'grid', gridId: g.id, name: g.name } },
          action: { name: 'view' },
          context: { ui: { app: 'experience', module: 'pages', pageId, widgetId } }
        });
      }
      for (const a of actions) {
        const widgetId = `action:${a?.id || a?.label || 'idx'}`;
        reqs.push({
          key: widgetId,
          resource: { type: 'page.widget', id: `${pageId}:${widgetId}`, properties: { widgetType: 'action', actionId: a?.id, label: a?.label } },
          action: { name: 'view' },
          context: { ui: { app: 'experience', module: 'pages', pageId, widgetId } }
        });
      }
    }
    return reqs;
  }, [pageId, grids, actions]);

  const batch = useBatchAuthorization(evals);

  // Data fetching for the selected grid
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['gridData', selectedGrid?.id, searchParams, pagination],
    queryFn: async () => {
      console.log('Query function called for grid:', selectedGrid?.id, selectedGrid?.name);
      if (!selectedGrid?.gridConfig?.dataSource) {
        console.log('No dataSource configured for grid:', selectedGrid);
        return null;
      }
      
      const { system, objectType, action: dsAction, params } = selectedGrid.gridConfig.dataSource;
      // Filter out empty search parameters like visual_designer
      const filteredSearchParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value !== "" && value != null)
      );

      const requestBody = {
        system,
        object_type: objectType,
        action: dsAction,
        params: {
          ...params,
          ...(selectedGrid.gridConfig.dataSource.isServerSidePagination ? {
            offset: pagination.pageSize * pagination.pageIndex,
            page_size: pagination.pageSize,
          } : {}),
          ...(Object.keys(filteredSearchParams).length > 0 ? { 
            search: filteredSearchParams
          } : {}),
        },
      };
      
      console.log('PreviewMode - making crud/execute request for grid:', selectedGrid?.name);
      console.log('PreviewMode - Request body:', requestBody);
      console.log('PreviewMode - dataFetchEnabled:', dataFetchEnabled);
      try {
        const response = await api.post('/crud/execute', requestBody);
        console.log('PreviewMode - crud/execute response:', response);
        // The API client returns the parsed JSON directly. Preserve full shape
        // so render can access either .result (object) or array payloads.
        // Handle both .data and .result response formats from Orchestration Service
        return (response as any)?.data ?? (response as any)?.result ?? response;
      } catch (err) {
        console.error('PreviewMode - crud/execute error:', err);
        throw err;
      }
    },
    enabled: dataFetchEnabled && !!selectedGrid?.gridConfig?.dataSource,
  });

  // Debug logging for query state
  React.useEffect(() => {
    console.log('Query state changed:', {
      selectedGridId: selectedGrid?.id,
      selectedGridName: selectedGrid?.name,
      dataFetchEnabled,
      hasDataSource: !!selectedGrid?.gridConfig?.dataSource,
      queryEnabled: dataFetchEnabled && !!selectedGrid?.gridConfig?.dataSource,
      isLoading,
      isFetching,
      error: error?.message,
      dataLength: Array.isArray(data) ? data.length : (data ? 'object' : 'null')
    });
  }, [selectedGrid, dataFetchEnabled, isLoading, isFetching, error, data]);

  useEffect(() => {
    const searchParamsFromGrid = selectedGrid?.gridConfig?.dataSource?.searchParams;
    setSearchParams(searchParamsFromGrid || {});
  }, [selectedGrid]);

  useEffect(() => {
    if (form) {
      setActiveForm({
        form,
        currentTaskId: null,
      });
    }
  }, [form, formId]);

  const handleSearch = (params: Record<string, string>) => {
    setSearchParams(params);
    setSelectedRows([]);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    
    if (!dataFetchEnabled) {
      setDataFetchEnabled(true);
    }
    
    setTimeout(() => refetch(), 100);
  };

  const handleRefresh = () => {
    setDataFetchEnabled(true);
    refetch();
  };

  // Action handling functions
  const handleActionClick = async (action: ActionConfig, rowsData: any[] | null = null) => {
    setActionLoading(action.id);

    if (rowsData && rowsData.length > 0) {
      setActiveRowsData(rowsData);
      setSelectedRows([]);
    }

    if (action.parameterMapping && Object.keys(action.parameterMapping).length > 0) {
      setParameterMapping(action.parameterMapping);
    }

    if (action.workflowName) {
      setWorkflowName(action.workflowName);
    }
    
    if (action.formId) {
      setFormId(action.formId);
    } else if (action.workflowName) {
      // Apply parameter mapping to row data before starting workflow
      let mappedData = action.acceptsArrayOfItems ? rowsData : rowsData?.[0];
      if (action.parameterMapping && Object.keys(action.parameterMapping).length > 0 && mappedData) {
        const dataToMap = action.acceptsArrayOfItems ? mappedData : [mappedData];
        const mapped = dataToMap.map((row: any) => {
          const result: Record<string, any> = {};
          Object.entries(action.parameterMapping!).forEach(([targetField, sourceField]) => {
            result[targetField] = row[sourceField];
          });
          return result;
        });
        mappedData = action.acceptsArrayOfItems ? mapped : mapped[0];
      }
      // Start workflow directly without form
      await startGraphWorkflow(action.workflowName, mappedData);
    }
    
    setActionLoading("");
  };

  const startGraphWorkflow = async (workflowName: string, data: any) => {
    try {
      const response = await executeGraphWorkflow(workflowName, data);
      const workflowStatus = response?.status;
      const currentTaskId = response?.result?.workflow?.current_task?.id;
      const workflowId = response?.result?.workflow?.id;
      const userCanResume = response?.result?.workflow?.current_task?.current_user_can_resume;
      const workflowForm = response?.result?.form;
      const workflowMessage = response?.message;
      
      handleRunWorkflow(
        workflowStatus,
        workflowForm,
        userCanResume,
        currentTaskId,
        workflowId,
        workflowMessage
      );
    } catch (error) {
      console.error('Workflow execution failed:', error);
      message.error(error instanceof Error ? error.message : "Workflow execution failed");
    }
  };

  const handleRunWorkflow = (
    workflowStatus: string,
    form: any,
    userCanResume: boolean,
    currentTaskId: string,
    workflowId?: string | null,
    workflowMessage?: string
  ) => {
    if (workflowStatus === "COMPLETED") {
      setActiveForm(null);
      setFormId("");
      message.success(workflowMessage?.trim() ? workflowMessage : "Workflow Completed");
      refetch();
      return;
    }

    if (workflowStatus === "ERROR") {
      setActiveForm(null);
      setFormId("");
      message.error(workflowMessage?.trim() ? workflowMessage : "Workflow Error");
      refetch();
      return;
    }

    if (userCanResume === false) {
      setActiveForm(null);
      setFormId("");
      message.info("Workflow Submitted for Approval. It can be resumed by authorized approver only");
      return;
    }

    // Check if the workflow is waiting for input
    if (workflowStatus) {
      if (form) {
        setActiveForm({
          form: form.schema,
          currentTaskId: currentTaskId,
          workflowId: workflowId,
          workflowName: workflowName || form.workflowName, // Pass workflow name for authorization
        });
        if (form.initial_data) {
          console.log('PreviewMode - setting prePopulatedData from workflow:', form.initial_data);
          setPrePopulatedData(form?.initial_data);
        } else {
          console.log('PreviewMode - no initial_data in workflow form response:', form);
        }
      } else {
        message.error(`No Form returned for the Workflow`);
      }
      return;
    }

    setActiveForm(null);
    setFormId("");
    message.warning(`No Action defined for Workflow Status : ${workflowStatus}`);
  };

  const resumeWorkflow = async (data: any, currentTaskId: string | null, workflowId?: string | null, workflowNameParam?: string) => {
    try {
      const response = await resumeGraphWorkflow(currentTaskId, data, workflowId, workflowNameParam);
      const workflowStatus = response?.status;
      const newCurrentTaskId = response?.result?.workflow?.current_task?.id;
      const newWorkflowId = response?.result?.workflow?.id || workflowId;
      const userCanResume = response?.result?.workflow?.current_user_can_resume;
      const workflowForm = response?.result?.form;
      const workflowMessage = response?.message;
      
      handleRunWorkflow(
        workflowStatus,
        workflowForm,
        userCanResume,
        newCurrentTaskId,
        newWorkflowId,
        workflowMessage
      );
    } catch (error) {
      console.error('Workflow resume failed:', error);
      message.error(error instanceof Error ? error.message : "Workflow resume failed");
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (activeForm?.currentTaskId) {
        // Resume workflow - pass workflowName for authorization
        await resumeWorkflow(data, activeForm.currentTaskId, activeForm.workflowId, activeForm.workflowName);
      } else {
        // Start new workflow
        await startGraphWorkflow(workflowName, data);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      message.error('Form submission failed');
    }
  };

  const combinedActions = React.useMemo(() => {
    return [
      ...(page.actions || []),
      ...(selectedGrid?.actions || [])
    ];
  }, [page.actions, selectedGrid]);

  const generalActions = combinedActions.filter(a => !a.contextual);
  const contextualActions = combinedActions.filter(a => a.contextual);

  return (
    <div style={{ background: 'var(--main-bg)', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {!hideExitButton && (
          <Button onClick={onExit} style={{ alignSelf: 'flex-start' }}>Exit Preview</Button>
        )}
        
        <div>
          <h1 style={{ color: 'var(--main-text)', margin: 0, marginBottom: '8px', fontSize: '28px', fontWeight: 600 }}>
            {page?.title || 'Page'}
          </h1>
          {page?.description && (
            <p style={{ color: 'var(--main-text-secondary)', fontSize: '14px', margin: 0 }}>
              {page.description}
            </p>
          )}
        </div>
      </div>

      {/* Main Layout */}
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
        {/* Main Content - Full Width */}
        <div style={{ flex: 1, paddingLeft: '32px', paddingRight: '32px' }}>
          {/* Tab Navigation and Create Actions Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            {/* Tab Navigation */}
            {grids.length > 0 && (
              <div style={{ display: 'flex', gap: '24px' }}>
                {grids.length > 1 ? (
                  grids.map((grid, index) => (
                    <button
                      key={grid.id}
                      type="button"
                      onClick={() => {
                        console.log('Tab switch clicked - Grid:', grid);
                        console.log('Tab switch - Grid config:', grid?.gridConfig);
                        console.log('Tab switch - Data source:', grid?.gridConfig?.dataSource);
                        setSelectedGrid(grid);
                        setSelectedRows([]);
                        setPagination({ pageIndex: 0, pageSize: 10 });
                        setSearchParams(grid?.gridConfig?.dataSource?.searchParams || {});
                        // Enable data fetching when switching tabs
                        setDataFetchEnabled(true);
                        setTimeout(() => refetch(), 100);
                      }}
                      style={{
                        background: selectedGrid?.id === grid.id ? 'var(--selected-bg)' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        color: selectedGrid?.id === grid.id ? 'var(--selected-text)' : 'var(--main-text-secondary)',
                        padding: '8px 16px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {grid.name || `Grid ${index + 1}`}
                    </button>
                  ))
                ) : (
                  <button
                    type="button"
                    style={{
                      background: 'var(--selected-bg)',
                      border: 'none',
                      borderRadius: '4px',
                      color: 'var(--selected-text)',
                      padding: '8px 16px',
                      fontSize: '14px'
                    }}
                  >
                    {selectedGrid?.name || 'Data'}
                  </button>
                )}
              </div>
            )}
            
            {/* Create User Actions - Top Right */}
            {generalActions.length > 0 && (
              <Space size="small">
                {generalActions.map((action: ActionConfig) => (
                  <Button 
                    key={action.id}
                    onClick={() => handleActionClick(action)}
                    disabled={actionLoading === action.id}
                    style={{ 
                      background: 'var(--selected-bg)', 
                      border: 'none', 
                      color: 'var(--selected-text)',
                      opacity: actionLoading === action.id ? 0.7 : 1
                    }}
                  >
                    {actionLoading === action.id ? 'Loading...' : (action.label || 'Action')}
                  </Button>
                ))}
              </Space>
            )}
          </div>

          {/* Search Controls */}
          <AdvancedSearchBar
            onSearch={handleSearch}
            searchParameters={searchParams}
            basicSearchKey="TextToSearch"
            isLoading={isFetching}
            onRefresh={handleRefresh}
            viewSwitcher={
              <GridViewSwitcher 
                view={gridView} 
                onViewChange={setGridView} 
              />
            }
          />

          {/* Content Area */}
          <div style={{ paddingBottom: '32px' }}>
            {error ? (
              <TileCard theme="auto" elevation={1}>
                <div style={{ padding: 16, color: 'var(--error-color)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Error Loading Data</div>
                  <div style={{ fontSize: '14px', opacity: 0.8 }}>
                    {error instanceof Error ? error.message : 'An unexpected error occurred'}
                  </div>
                </div>
              </TileCard>
            ) : !dataFetchEnabled ? (
              <TileCard theme="auto" elevation={1}>
                <div style={{ padding: 32, textAlign: 'center' }}>
                  <h3 style={{ margin: 0, marginBottom: 12, fontSize: '18px' }}>Preview Mode</h3>
                  <p style={{ margin: 0, marginBottom: 8, opacity: 0.7 }}>
                    Click the refresh button above to load data and test your page configuration.
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.6 }}>
                    This prevents unnecessary API calls during page design.
                  </p>
                </div>
              </TileCard>
            ) : selectedGrid?.gridConfig ? (
              <div>
                <PreviewGrid
                  columns={(selectedGrid.gridConfig?.columns || []).map((c: any) => (typeof c === 'string' ? c : (c.key || c.dataIndex || c.field || c.name || c.title)))}
                  data={Array.isArray(data) ? data : (data?.result || [])}
                  isLoading={isLoading}
                  selectedRows={selectedRows}
                  onSelectRows={setSelectedRows}
                  onPaginationChange={setPagination}
                  pagination={pagination}
                  serverSidePagination={Boolean(selectedGrid?.gridConfig?.dataSource?.isServerSidePagination)}
                  rowCount={!Array.isArray(data) ? (data as any)?.total_count : undefined}
                  enableSelection={true}
                  multiRowSelection={true}
                  view={gridView}
                  onViewChange={setGridView}
                  rowActions={contextualActions.length > 0 ? contextualActions : undefined}
                  onRowAction={(action, row) => handleActionClick(action, [row])}
                  actionLoading={actionLoading}
                />
              </div>
            ) : (
              <TileCard theme="auto" elevation={1}>
                <div style={{ padding: 32, textAlign: 'center', opacity: 0.7 }}>
                  No grid configuration available
                </div>
              </TileCard>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {formLoading && formId && !activeForm && (
        <FormModalSkeleton onClose={() => setFormId("")} />
      )}

      {activeForm && (
        <FormModal
          form={activeForm.form}
          initialData={activeRowsData ? activeRowsData[activeRowsData.length - 1] : null}
          prePopulatedData={prePopulatedData}
          parameterMapping={parameterMapping}
          onClose={() => {
            setActiveForm(null);
            setFormId("");
            refetch();
          }}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}


